/**
 * Pure logic for the EMAIL_RULES editor (contacts §4A.3-bis; adopted gateway
 * face 2026-07-16: `email.rules_get/set`, whole-list semantics). The SPA-side
 * confirm restates each CHANGED rule's exact effect before `email.rules_set`
 * — the same restate discipline the runtime applies in its own confirm loop.
 */
import type { EmailRule } from "@/types/contacts";

export interface RuleChange {
  kind: "added" | "edited" | "enabled" | "disabled" | "removed";
  rule: EmailRule;
}

/** Human restatement of one rule's effect (shown in the confirm). */
export function restateRule(rule: EmailRule): string {
  return `Quando: ${rule.when.trim()} → Então: ${rule.then.trim()}`;
}

/** Diff prev → next (by id) into the changes the confirm must restate. */
export function diffRules(prev: readonly EmailRule[], next: readonly EmailRule[]): RuleChange[] {
  const prevById = new Map(prev.map((r) => [r.id, r]));
  const nextById = new Map(next.map((r) => [r.id, r]));
  const changes: RuleChange[] = [];

  for (const rule of next) {
    const before = prevById.get(rule.id);
    if (!before) {
      changes.push({ kind: "added", rule });
    } else if (before.when !== rule.when || before.then !== rule.then) {
      changes.push({ kind: "edited", rule });
    } else if (before.enabled !== rule.enabled) {
      changes.push({ kind: rule.enabled ? "enabled" : "disabled", rule });
    }
  }
  for (const rule of prev) {
    if (!nextById.has(rule.id)) changes.push({ kind: "removed", rule });
  }
  return changes;
}

/** A rule is saveable when both halves are non-empty. */
export function ruleViolation(rule: Pick<EmailRule, "when" | "then">): string | null {
  if (!rule.when.trim()) return "A condição (“quando”) não pode ficar vazia.";
  if (!rule.then.trim()) return "A instrução (“então”) não pode ficar vazia.";
  return null;
}

/** Client-side id for a NEW rule (stable until the runtime echoes the list
 *  back; prefixed so the runtime can recognize fresh entries). */
export function newRuleId(existing: readonly EmailRule[]): string {
  let n = existing.length + 1;
  const ids = new Set(existing.map((r) => r.id));
  while (ids.has(`rule-new-${n}`)) n += 1;
  return `rule-new-${n}`;
}
